import React, { useEffect, lazy, Suspense } from "react";
import { 
  Save, 
  AlertCircle, 
  Loader2,
  RefreshCw
} from 'lucide-react';
import { useParams, useNavigate, Routes, Route, Navigate, NavLink } from 'react-router-dom';
import { useRegistrationRules, RegistrationRulesProvider } from '../../../contexts/RegistrationRulesContext';
import ErrorBoundary from '../../../components/ErrorBoundary';
import ConfirmModalProvider from '../../../components/admin/ConfirmModal';

// Lazy-loaded tab components
const AgeGroupsTab = lazy(() => import('../../../components/admin/AgeGroupsTab'));
const StyleCategoriesTab = lazy(() => import('../../../components/admin/StyleCategoriesTab'));
const CompetitionCategoriesTab = lazy(() => import('../../../components/admin/CompetitionCategoriesTab'));
const StyleSpecificScoreSheetsTab = lazy(() => import('../../../components/admin/StyleSpecificScoreSheetsTab'));

// Loading component for suspense fallback
const TabLoader = () => (
  <div className="flex justify-center items-center p-8">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

/**
 * RegistrationRulesContent
 *
 * Main content component that uses the RegistrationRules context
 */
const RegistrationRulesContent = () => {
  const { state, loadSettings, saveSettings, resetSettings } = useRegistrationRules();
  const { isLoading, notification, hasUnsavedChanges } = state;
  
  // Get the current tab from URL params for error messages
  const { tab = 'ageGroups' } = useParams();
  
  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  // Add beforeunload event listener
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Custom error fallback for tab content
  const tabErrorFallback = (error) => (
    <div className="p-6 border border-error/20 bg-error/5 rounded-lg flex items-start gap-4">
      <div className="text-error mt-1">
        <AlertCircle size={24} />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-error mb-2">Tab Content Error</h3>
        <p className="text-base-content/80">
          There was an error loading the {tab} tab content. You can try switching to another tab or refreshing the page.
        </p>
        {error && (
          <pre className="mt-4 p-3 bg-base-300 rounded text-sm overflow-auto max-h-48">
            {error.message}
          </pre>
        )}
      </div>
    </div>
  );

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="rounded-box p-6 bg-base-100 shadow-xl">
        <div className="flex flex-col lg:flex-row justify-between gap-6">
          <div>
            <h1 className="text-4xl font-bold text-base-content">
              Age Divisions & Categories
            </h1>
            <p className="text-base-content/60 mt-2">
              Define age groups, determination methods, and style categories for competitions
            </p>
          </div>
          <div className="flex items-center">
            <button 
              onClick={resetSettings} 
              className="btn btn-outline mr-3"
              disabled={isLoading}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset
            </button>
            <button 
              onClick={saveSettings} 
              className="btn btn-primary"
              disabled={isLoading || !hasUnsavedChanges}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save Divisions
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`alert ${notification.type === 'error' ? 'alert-error' : 'alert-success'} flex items-center`}>
          <AlertCircle className="w-5 h-5 mr-2" />
          <span>{notification.message}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body p-6">
          <div className="tabs tabs-boxed" role="tablist">
            <NavLink
              to="/admin/settings/registration-rules/ageGroups"
              end
              className={({ isActive }) =>
                `tab border border-base-300 hover:bg-base-200 transition-all duration-200 ${
                  isActive ? 'tab-active' : ''
                }`
              }
              role="tab"
            >
              Age Groups
            </NavLink>
            <NavLink
              to="/admin/settings/registration-rules/styleCategories"
              end
              className={({ isActive }) =>
                `tab border border-base-300 hover:bg-base-200 transition-all duration-200 ${
                  isActive ? 'tab-active' : ''
                }`
              }
              role="tab"
            >
              Style Categories
            </NavLink>
            <NavLink
              to="/admin/settings/registration-rules/competitionCategories"
              end
              className={({ isActive }) =>
                `tab border border-base-300 hover:bg-base-200 transition-all duration-200 ${
                  isActive ? 'tab-active' : ''
                }`
              }
              role="tab"
            >
              Competition Categories
            </NavLink>
            <NavLink
              to="/admin/settings/registration-rules/styleScoreSheets"
              end
              className={({ isActive }) =>
                `tab border border-base-300 hover:bg-base-200 transition-all duration-200 ${
                  isActive ? 'tab-active' : ''
                }`
              }
              role="tab"
            >
              Style Score Sheets
            </NavLink>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 gap-6">
        {/* Lazy-loaded, route-based tab content with error boundary */}
        <ErrorBoundary fallback={tabErrorFallback}>
          <Suspense fallback={<TabLoader />}>
            <Routes>
              <Route path="ageGroups" element={<AgeGroupsTab />} />
              <Route path="styleCategories" element={<StyleCategoriesTab />} />
              <Route path="competitionCategories" element={<CompetitionCategoriesTab />} />
              <Route path="styleScoreSheets" element={<StyleSpecificScoreSheetsTab />} />
              <Route path="*" element={<Navigate to="ageGroups" replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
};

/**
 * RegistrationRulesSettings
 *
 * Wrapper component that provides the context
 */
const RegistrationRulesSettings = () => (
  <RegistrationRulesProvider>
    <ConfirmModalProvider>
      <RegistrationRulesContent />
    </ConfirmModalProvider>
  </RegistrationRulesProvider>
);

export default RegistrationRulesSettings; 